"use client"

import React, { useState, useMemo } from "react"
import { FixedSizeList as List } from "react-window"
import { Check } from "lucide-react"
import { Input } from "./input"
import { Label } from "./label"
import {
    Popover,
    PopoverContent,
    PopoverTrigger,
} from "./popover"
import { cn } from "../../lib/utils"
import { ChevronDown, Filter, X } from "lucide-react"
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "./select"
import { Button } from "./button"
import { Calendar } from "./calendar"
import { Popover as CalendarPopover, PopoverContent as CalendarPopoverContent, PopoverTrigger as CalendarPopoverTrigger } from "./popover"
import { format } from "date-fns"
import type { FilterableOption } from "./multi-select-with-filters"

export const SingleSelectWithFilters = ({
    options,
    selectedValue,
    onSelectionChange,
    placeholder,
    searchPlaceholder = "Search...",
    organizationOptions = [],
}: {
    options: FilterableOption[]
    selectedValue: string
    onSelectionChange: (value: string) => void
    placeholder: string
    searchPlaceholder?: string
    organizationOptions?: string[]
}) => {
    const [isOpen, setIsOpen] = useState(false)
    const [searchTerm, setSearchTerm] = useState("")
    const [selectedOrganization, setSelectedOrganization] = useState<string>("all")
    const [selectedDate, setSelectedDate] = useState<Date | undefined>(undefined)

    // Filter options based on search, organization, and date
    const filteredOptions = useMemo(() => {
        let filtered = options

        // Filter by search term (name, role)
        if (searchTerm) {
            const searchLower = searchTerm.toLowerCase()
            filtered = filtered.filter(
                (option) =>
                    option.name.toLowerCase().includes(searchLower) ||
                    (option.role && option.role.toLowerCase().includes(searchLower)) ||
                    (option.organization && option.organization.toLowerCase().includes(searchLower))
            )
        }

        // Filter by organization
        if (selectedOrganization && selectedOrganization !== "all") {
            filtered = filtered.filter(
                (option) => option.organization === selectedOrganization
            )
        }

        // Filter by date serving
        if (selectedDate) {
            filtered = filtered.filter((option) => {
                if (!option.arrivalDate && !option.departureDate) return true
                const filterDate = selectedDate.toISOString().split("T")[0]
                if (option.arrivalDate && option.departureDate) {
                    return filterDate >= option.arrivalDate && filterDate <= option.departureDate
                }
                if (option.arrivalDate) return filterDate >= option.arrivalDate
                if (option.departureDate) return filterDate <= option.departureDate
                return true
            })
        }

        return filtered
    }, [options, searchTerm, selectedOrganization, selectedDate])

    const handleSelect = (value: string) => {
        onSelectionChange(value)
        setSearchTerm("")
        setIsOpen(false)
    }

    const selectedName = selectedValue
        ? options.find((o) => o.id === selectedValue)?.name ?? ""
        : ""

    const hasActiveFilters = selectedOrganization !== "all" || selectedDate !== undefined

    const clearFilters = () => {
        setSelectedOrganization("all")
        setSelectedDate(undefined)
        setSearchTerm("")
    }

    const showNotAssigned = selectedValue !== ""
    const listItemCount = filteredOptions.length + (showNotAssigned ? 1 : 0)

    const Row = ({
        index,
        style,
    }: {
        index: number
        style: React.CSSProperties
    }) => {
        if (showNotAssigned && index === 0) {
            return (
                <div
                    style={style}
                    className="flex items-center space-x-2 p-2 hover:bg-accent rounded-sm cursor-pointer"
                    onClick={() => handleSelect("")}
                >
                    <div className="flex items-center justify-center w-5 h-5 shrink-0" />
                    <span className="text-sm text-muted-foreground">Not assigned</span>
                </div>
            )
        }
        const option = filteredOptions[showNotAssigned ? index - 1 : index]
        if (!option) return null

        return (
            <div
                style={style}
                className="flex items-center space-x-2 p-2 hover:bg-accent rounded-sm cursor-pointer"
                onClick={() => handleSelect(option.id)}
            >
                <div className="flex items-center justify-center w-5 h-5 shrink-0">
                    {selectedValue === option.id ? (
                        <Check className="w-4 h-4 text-primary" />
                    ) : (
                        <div className="w-4 h-4" />
                    )}
                </div>
                <div className="flex flex-col flex-1 min-w-0">
                    <span className="text-sm text-foreground font-medium">
                        {option.name}
                    </span>
                    {(option.organization || option.role) && (
                        <div className="flex items-center gap-2 text-xs text-muted-foreground">
                            {option.organization && <span>{option.organization}</span>}
                            {option.organization && option.role && <span>•</span>}
                            {option.role && <span>{option.role}</span>}
                        </div>
                    )}
                </div>
            </div>
        )
    }

    return (
        <Popover
            open={isOpen}
            onOpenChange={(open) => {
                setIsOpen(open)
                if (!open) setSearchTerm("")
            }}
        >
            <PopoverTrigger asChild>
                <div
                    role="combobox"
                    aria-expanded={isOpen}
                    aria-controls="combobox-popover-content"
                    tabIndex={0}
                    onKeyDown={(e) => {
                        if (e.key === "Enter" || e.key === " ") {
                            e.preventDefault()
                            setIsOpen(!isOpen)
                        }
                    }}
                    className={cn(
                        "flex h-14 w-full items-center justify-between rounded border border-input bg-white px-3 py-2 text-sm shadow-sm ring-offset-background placeholder:text-muted-foreground focus:outline-none disabled:cursor-not-allowed disabled:opacity-50 cursor-pointer focus:border-primary",
                        isOpen && "border-primary"
                    )}
                >
                    <span
                        className={selectedName ? "text-foreground truncate" : "text-muted-foreground/60"}
                    >
                        {selectedName || placeholder}
                    </span>
                    <div className="flex items-center gap-1 shrink-0 ml-2">
                        {hasActiveFilters && (
                            <div className="h-2 w-2 rounded-full bg-primary" />
                        )}
                        <ChevronDown className="h-4 w-4 opacity-50" />
                    </div>
                </div>
            </PopoverTrigger>
            <PopoverContent className="w-full p-0" align="start">
                <div className="p-2 space-y-2">
                    <Input
                        placeholder={searchPlaceholder}
                        value={searchTerm}
                        onChange={(e) =>
                            setSearchTerm((e.target as HTMLInputElement).value)
                        }
                        className="h-9 text-sm"
                    />

                    <div className="flex flex-col gap-2">
                        <div className="flex items-center justify-between">
                            <Label className="text-xs font-semibold text-muted-foreground flex items-center gap-1">
                                <Filter className="h-3 w-3" />
                                Filters
                            </Label>
                            {hasActiveFilters && (
                                <Button
                                    variant="ghost"
                                    size="sm"
                                    className="h-6 px-2 text-xs"
                                    onClick={clearFilters}
                                >
                                    <X className="h-3 w-3 mr-1" />
                                    Clear
                                </Button>
                            )}
                        </div>

                        <div className="grid grid-cols-2 gap-2">
                            <div className="space-y-1">
                                <Label className="text-xs text-muted-foreground">
                                    Organization
                                </Label>
                                <Select
                                    value={selectedOrganization}
                                    onValueChange={setSelectedOrganization}
                                >
                                    <SelectTrigger className="h-8 text-xs">
                                        <SelectValue placeholder="All" />
                                    </SelectTrigger>
                                    <SelectContent>
                                        <SelectItem value="all">All Organizations</SelectItem>
                                        {organizationOptions.map((org) => (
                                            <SelectItem key={org} value={org}>
                                                {org}
                                            </SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                            </div>

                            <div className="space-y-1">
                                <Label className="text-xs text-muted-foreground">
                                    Date Serving
                                </Label>
                                <CalendarPopover>
                                    <CalendarPopoverTrigger asChild>
                                        <Button
                                            variant="outline"
                                            className={cn(
                                                "h-8 w-full justify-start text-left text-xs",
                                                !selectedDate && "text-muted-foreground"
                                            )}
                                        >
                                            {selectedDate
                                                ? format(selectedDate, "MMM d, yyyy")
                                                : "Any date"}
                                        </Button>
                                    </CalendarPopoverTrigger>
                                    <CalendarPopoverContent className="w-auto p-0" align="start">
                                        <Calendar
                                            mode="single"
                                            selected={selectedDate}
                                            onSelect={setSelectedDate}
                                            initialFocus
                                        />
                                        {selectedDate && (
                                            <div className="p-2 border-t">
                                                <Button
                                                    variant="ghost"
                                                    size="sm"
                                                    className="w-full h-7 text-xs"
                                                    onClick={() => setSelectedDate(undefined)}
                                                >
                                                    Clear date
                                                </Button>
                                            </div>
                                        )}
                                    </CalendarPopoverContent>
                                </CalendarPopover>
                            </div>
                        </div>
                    </div>

                    {hasActiveFilters && (
                        <div className="text-xs text-muted-foreground px-1">
                            {filteredOptions.length} {filteredOptions.length === 1 ? "result" : "results"}
                        </div>
                    )}

                    <div className="max-h-60">
                        {listItemCount > 0 ? (
                            <List
                                height={Math.min(240, listItemCount * 50)}
                                itemCount={listItemCount}
                                itemSize={50}
                                width="100%"
                                className="scrollbar-thin scrollbar-thumb-gray-300 scrollbar-track-gray-100"
                            >
                                {Row}
                            </List>
                        ) : (
                            <div className="p-4 text-center text-sm text-muted-foreground">
                                No results found
                            </div>
                        )}
                    </div>
                </div>
            </PopoverContent>
        </Popover>
    )
}
